import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material'
import {
  Upload as UploadIcon,
  CloudUpload,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import PageHeader from '../components/ui/PageHeader'
import { useBulkImportLeads } from '../hooks/useLeads'
import { BulkImportResult } from '../types/lead'

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Strip the "data:<mime>;base64," prefix
      const base64 = result.substring(result.indexOf(',') + 1)
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

const LeadImportPage: React.FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const bulkImport = useBulkImportLeads()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const lowerName = file.name.toLowerCase()
    const hasValidExtension = ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))
    if (!hasValidExtension) {
      enqueueSnackbar('Please select a CSV or Excel (.xlsx/.xls) file', { variant: 'error' })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar('File size must be less than 10MB', { variant: 'error' })
      return
    }

    setSelectedFile(file)
    setImportResult(null)
  }

  const handleReset = () => {
    setSelectedFile(null)
    setImportResult(null)
    setShowErrors(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleImport = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Please select a file', { variant: 'warning' })
      return
    }

    const lowerName = selectedFile.name.toLowerCase()
    const fileType: 'csv' | 'excel' = lowerName.endsWith('.csv') ? 'csv' : 'excel'

    try {
      const fileContent = await fileToBase64(selectedFile)
      const result = await bulkImport.mutateAsync({ file_content: fileContent, file_type: fileType })
      setImportResult(result)
    } catch (error) {
      // Error notification already handled by useBulkImportLeads
    }
  }

  return (
    <Box>
      <PageHeader
        title="Import Leads"
        subtitle="Bulk import leads from a CSV or Excel file"
        actions={[
          { label: 'Back to Leads', onClick: () => navigate('/admin/leads'), variant: 'outlined' },
        ]}
      />

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>File Format Requirements:</strong>
              </Typography>
              <Typography variant="caption" component="div">
                • Include headers: name, phone, email, company, source, location, city, state
              </Typography>
              <Typography variant="caption" component="div">
                • Accepted formats: CSV (.csv) or Excel (.xlsx, .xls)
              </Typography>
              <Typography variant="caption" component="div">
                • Phone number must be unique for each lead
              </Typography>
              <Typography variant="caption" component="div">
                • Maximum file size: 10MB
              </Typography>
            </Alert>

            {!importResult && (
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: selectedFile ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: selectedFile ? 'primary.50' : 'grey.50',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                {selectedFile ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleReset()
                      }}
                      sx={{ mt: 2 }}
                    >
                      Choose Different File
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Click to select a CSV or Excel file
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or drag and drop here
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {bulkImport.isPending && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Importing leads...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {importResult && (
              <Box>
                <Alert severity={importResult.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Import Complete</strong>
                  </Typography>
                  <Typography variant="caption" component="div">
                    Total rows: {importResult.total_rows}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Successful: {importResult.successful}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Failed: {importResult.failed}
                  </Typography>
                </Alert>

                {importResult.errors.length > 0 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="error">
                        Import Errors ({importResult.errors.length})
                      </Typography>
                      <IconButton size="small" onClick={() => setShowErrors(!showErrors)}>
                        {showErrors ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Collapse in={showErrors}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Row</TableCell>
                              <TableCell>Error Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {importResult.errors.slice(0, 10).map((error, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Chip label={error.row ?? 'N/A'} size="small" color="error" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">{error.error}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {importResult.errors.length > 10 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Showing first 10 of {importResult.errors.length} errors
                        </Typography>
                      )}
                    </Collapse>
                  </Box>
                )}

                <Button variant="outlined" onClick={handleReset} fullWidth sx={{ mt: 2 }}>
                  Import Another File
                </Button>
              </Box>
            )}

            {!importResult && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleImport}
                  variant="contained"
                  startIcon={<UploadIcon />}
                  disabled={!selectedFile || bulkImport.isPending}
                >
                  {bulkImport.isPending ? 'Importing...' : 'Import'}
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default LeadImportPage
